/**
 * useAssistantActivities — background work started from the assistant thread
 * (Flow Autobuild or full Maintenance), for the panel's activity strip.
 *
 * Running autobuild activities are also polled against
 * `/workflows/:id/autobuild-status` every 10s to surface a live pipeline
 * stage. Maintenance completion arrives through activity polling/SSE and must
 * never query the autobuild endpoint.
 */

import { useQueries, useQuery } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import { ASSISTANT_ACTIVITIES_QUERY_KEY } from './capabilityInvalidation';

export type AssistantActivityOutcome =
  | 'converged'
  | 'unverified'
  | 'blocked'
  | 'error'
  | 'not_testable'
  | 'spec_review_failed'
  | 'canceled'
  | 'partially_converged'
  | 'awaiting_review';

export type AssistantActivityKind = 'flow_autobuild' | 'flow_maintenance';

export type AssistantActivity = {
  id: string;
  kind: AssistantActivityKind;
  workflowId: string;
  status: 'running' | 'completed';
  outcome: AssistantActivityOutcome | null;
  title: string;
  createdAt: string;
  updatedAt: string;
};

export type AutobuildStage = 'spec_review' | 'authoring' | 'converging' | 'verifying' | 'completed';

const ACTIVITIES_KEY = ASSISTANT_ACTIVITIES_QUERY_KEY;
const AUTOBUILD_STATUS_POLL_MS = 10_000;
const ACTIVITY_POLL_MS = 10_000;

async function fetchActivities(conversationId: string): Promise<AssistantActivity[]> {
  const params = new URLSearchParams({ conversationId });
  const res = await fetch(`${API_URL}/assistant/activities?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res, 'Failed to load assistant activities');
  const json = (await res.json()) as { ok: boolean; data: AssistantActivity[] };
  return json.data;
}

async function fetchAutobuildStage(workflowId: string): Promise<AutobuildStage | null> {
  const res = await fetch(`${API_URL}/workflows/${workflowId}/autobuild-status`, {
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res, 'Failed to load autobuild status');
  const json = (await res.json()) as { ok: boolean; autobuild: { stage: AutobuildStage } | null };
  return json.autobuild?.stage ?? null;
}

export type AssistantActivityWithStage = AssistantActivity & { stage: AutobuildStage | null };

export function useAssistantActivities(options: {
  conversationId: string | null;
  enabled: boolean;
}) {
  const { conversationId } = options;
  const enabled = options.enabled && !!conversationId;

  const activitiesQuery = useQuery({
    queryKey: [...ACTIVITIES_KEY, options.conversationId],
    queryFn: () => fetchActivities(conversationId ?? ''),
    enabled,
    staleTime: 15_000,
    // The wake-up event invalidates this the moment work finishes, so polling
    // is not how a completion normally arrives. It is here so a dropped SSE
    // connection doesn't leave the strip stuck on "running" forever — and it
    // stops on its own once nothing is running.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((activity) => activity.status === 'running')
        ? ACTIVITY_POLL_MS
        : false,
  });

  const activities = activitiesQuery.data ?? [];
  const runningActivities = activities.filter((activity) => activity.status === 'running');
  const runningAutobuildActivities = runningActivities.filter(
    (activity) => activity.kind === 'flow_autobuild'
  );

  const stageQueries = useQueries({
    queries: runningAutobuildActivities.map((activity) => ({
      queryKey: ['assistant', 'autobuild-stage', activity.workflowId],
      queryFn: () => fetchAutobuildStage(activity.workflowId),
      enabled,
      refetchInterval: AUTOBUILD_STATUS_POLL_MS,
    })),
  });

  const stageByWorkflowId = new Map<string, AutobuildStage | null>();
  runningAutobuildActivities.forEach((activity, index) => {
    stageByWorkflowId.set(activity.workflowId, stageQueries[index]?.data ?? null);
  });

  const activitiesWithStage: AssistantActivityWithStage[] = activities.map((activity) => ({
    ...activity,
    stage:
      activity.kind === 'flow_autobuild' && activity.status === 'running'
        ? (stageByWorkflowId.get(activity.workflowId) ?? null)
        : null,
  }));

  return {
    activities: activitiesWithStage,
    isLoading: activitiesQuery.isLoading,
  };
}
